import { useState, useEffect } from 'react';
import api from '../../services/api';

const actionStyles = {
    created: { bg: '#dcfce7', color: '#16a34a', label: '✅ Créé' },
    updated: { bg: '#fef9c3', color: '#ca8a04', label: '✏️ Modifié' },
    deleted: { bg: '#fee2e2', color: '#dc2626', label: '🗑️ Supprimé' },
    login:   { bg: '#dbeafe', color: '#2563eb', label: '🔑 Connexion' },
};

const ITEMS_PER_PAGE = 100;

export default function LogManager() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);

    useEffect(() => {
        setLoading(true);
        api.get(`/logs?order[createdAt]=desc&itemsPerPage=${ITEMS_PER_PAGE}&page=${page}`)
            .then(res => {
                setLogs(res.data.member || []);
                setTotalItems(res.data['totalItems'] ?? res.data['hydra:totalItems'] ?? 0);
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [page]);

    const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));

    const goToPage = (p) => {
        if (p < 1 || p > totalPages) return;
        setPage(p);
    };

    // Génère une liste de numéros de page compacte (ex: 1 ... 4 5 [6] 7 8 ... 12)
    const getPageNumbers = () => {
        const delta = 2;
        const range = [];
        for (let i = Math.max(1, page - delta); i <= Math.min(totalPages, page + delta); i++) {
            range.push(i);
        }
        return range;
    };

    if (loading) return <div style={{ padding: '20px', color: '#888' }}>Chargement...</div>;

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>📋 Logs d'activité</h2>
                <span style={{ fontSize: '13px', color: '#888' }}>{totalItems} entrée{totalItems > 1 ? 's' : ''} au total</span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
                {logs.length === 0 && (
                    <div style={{ color: '#888', textAlign: 'center', padding: '40px' }}>
                        Aucun log pour le moment
                    </div>
                )}
                {logs.map(log => {
                    const style = actionStyles[log.action] || { bg: '#f0f0f0', color: '#666', label: log.action };
                    return (
                        <div key={log.id} style={{
                            backgroundColor: 'white',
                            border: '1px solid #e0e0e0',
                            borderRadius: '8px',
                            padding: '12px 16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '16px',
                            fontSize: '13px',
                        }}>
                            <span style={{
                                backgroundColor: style.bg,
                                color: style.color,
                                padding: '3px 8px',
                                borderRadius: '6px',
                                fontWeight: '600',
                                whiteSpace: 'nowrap',
                                fontSize: '12px',
                            }}>
                                {style.label}
                            </span>
                            <span style={{ flex: 1, color: '#333' }}>
                                {log.description}
                            </span>
                            <span style={{ color: '#888', whiteSpace: 'nowrap' }}>
                                {log.user?.nom || log.user?.email || '?'}
                            </span>
                            <span style={{ color: '#aaa', whiteSpace: 'nowrap' }}>
                                {new Date(log.createdAt).toLocaleString('fr-FR')}
                            </span>
                        </div>
                    );
                })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px' }}>
                    <button onClick={() => goToPage(1)} disabled={page === 1} style={pageButtonStyle(page === 1)}>
                        « Première
                    </button>
                    <button onClick={() => goToPage(page - 1)} disabled={page === 1} style={pageButtonStyle(page === 1)}>
                        ‹ Précédent
                    </button>

                    {getPageNumbers()[0] > 1 && <span style={{ padding: '0 6px', color: '#888' }}>…</span>}

                    {getPageNumbers().map(p => (
                        <button
                            key={p}
                            onClick={() => goToPage(p)}
                            style={{
                                ...pageButtonStyle(false),
                                backgroundColor: p === page ? 'var(--accent-color)' : 'white',
                                color: p === page ? 'white' : '#444',
                                fontWeight: p === page ? '700' : '500',
                            }}
                        >
                            {p}
                        </button>
                    ))}

                    {getPageNumbers()[getPageNumbers().length - 1] < totalPages && <span style={{ padding: '0 6px', color: '#888' }}>…</span>}

                    <button onClick={() => goToPage(page + 1)} disabled={page === totalPages} style={pageButtonStyle(page === totalPages)}>
                        Suivant ›
                    </button>
                    <button onClick={() => goToPage(totalPages)} disabled={page === totalPages} style={pageButtonStyle(page === totalPages)}>
                        Dernière »
                    </button>
                </div>
            )}
        </div>
    );
}

const pageButtonStyle = (disabled) => ({
    padding: '7px 12px',
    borderRadius: '6px',
    border: '1px solid #e0e0e0',
    backgroundColor: 'white',
    color: disabled ? '#ccc' : '#444',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: '13px',
    fontWeight: '500',
});